import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column, Flex } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import React from "react";
import styled from "styled-components";

export const LoggedOutCategoryPageHeroBanner: React.FC<{
  layoutElement: ILayoutElement;
}> = ({ layoutElement }) => {
  const {
    background_image = IImage.build(),
    main_text,
    description_text,
    background_color,
  } = layoutElement;

  const isSSR = useIsSSR();

  const imgSrc = useResponsiveValue({
    mobileAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 454,
      height: 214,
      resizeMethod: "fit",
    }),
    tabletAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 700,
      height: 325,
      resizeMethod: "fit",
    }),
    desktopAndAbove: optimizeImageUrl(background_image, isSSR, {
      width: 800,
      height: 372,
      resizeMethod: "fit",
    }),
    xxLargeDesktop: optimizeImageUrl(background_image, isSSR, {
      width: 1200,
      height: 558,
      resizeMethod: "fit",
    }),
  });

  return (
    <Wrapper $bannerColor={background_color}>
      <BannerTextWrapper>
        <ContentWrapper>
          <Typography
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              tabletAndAbove: "displaySSerifRegular",
              xLargeAndAbove: "displayMSerifRegular",
            }}
            as="h1"
            color={Core.text.inverse}
          >
            {main_text}
          </Typography>
          <Typography
            variant={{
              mobileAndAbove: "paragraphSansRegular",
              xLargeAndAbove: "subheadingSansRegular",
            }}
            color={Core.text.inverse}
          >
            {description_text}
          </Typography>
        </ContentWrapper>
      </BannerTextWrapper>
      <BannerImageWrapper>
        <BannerImage
          src={imgSrc}
          data-test-id="banner-image"
          alt={background_image?.alt_text}
        />
      </BannerImageWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column-reverse", tabletAndAbove: "row" },
})<{ $bannerColor?: string }>`
  width: 100%;
  background-color: ${({ $bannerColor }) => $bannerColor};
  margin-bottom: ${getSpacing("3x")};
  ${responsiveMediaQueries.desktop} {
    margin-bottom: ${getSpacing("4x")};
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    margin-bottom: ${getSpacing("5x")};
  }
`;

const BannerTextWrapper = styled(Flex).attrs({
  direction: "column",
  justify: "space-between",
  align: "flex-start",
  gap: "3x",
})`
  flex-basis: 50%;
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 672px;
    width: 672px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: ${getSpacing(133, "6x")};
  }
  ${responsiveMediaQueries.desktop} {
    padding: ${getSpacing("10x", "6x")};
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("8x", "4x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", "2x")};
  }
`;

const BannerImageWrapper = styled(Flex)`
  flex-basis: 50%;
  ${responsiveMediaQueries.mobile} {
    max-height: 176px;
  }
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 100%;
  }
`;

const ContentWrapper = styled(Column).attrs({
  align: "flex-start",
  gap: { mobileAndAbove: "1/2x", tabletAndAbove: "2x" },
})`
  word-break: break-word;
  width: 100%;
  ${responsiveMediaQueries.xxlarge} {
    width: 513px;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  object-fit: cover;
  height: 0;
  min-height: 100%;
  object-position: center;
  ${responsiveMediaQueries.mobile} {
    height: 100%;
    max-height: 176px;
  }
`;
